import { LinearResampler, StreamingTranscriber, float32ToPcm16 } from 'assemblyai/streaming';
import { apiPost } from '../config/api';

/** Temporary streaming tokens expire in at most 600s (AssemblyAI limit). */
const TOKEN_TTL_SECONDS = 300;
const TOKEN_REFRESH_BUFFER_MS = 30 * 1000;
/** AssemblyAI streaming expects PCM16 mono at 16 kHz. */
const TARGET_SAMPLE_RATE = 16000;

function mapAssemblySpeaker(label) {
  if (!label || label === 'UNKNOWN') return 'UU';
  if (/^S\d+$/i.test(label)) return label.toUpperCase();
  if (/^[A-Z]$/i.test(label)) {
    const n = label.toUpperCase().charCodeAt(0) - 64;
    return `S${n}`;
  }
  return label;
}

function toError(error, fallback = 'Transcription error — please try again.') {
  if (error instanceof Error && error.message) return error;
  if (typeof error === 'string' && error.trim()) return new Error(error);
  if (error?.message) return new Error(String(error.message));
  return new Error(fallback);
}

class AssemblyAiService {
  constructor() {
    this.transcriber = null;
    this.onTranscript = null;
    this.onDiarizedTranscript = null;
    this.onFinal = null;
    this.onError = null;
    this.onConnected = null;
    this.onDisconnected = null;
    this.finalTranscript = '';
    this.interimTranscript = '';
    this.interimSpeaker = 'UU';
    this.utterances = [];
    this.mode = 'default';
    this.intentionalClose = false;
    this.tokenCache = null;
    this.tokenFetchPromise = null;
    this.sampleRate = TARGET_SAMPLE_RATE;
    this.sourceSampleRate = TARGET_SAMPLE_RATE;
    this.resampler = null;
    this.recognitionReady = false;
    this.audioQueue = [];
    this.pendingPcmChunks = [];
    this.pendingPcmSamples = 0;
    this.finalizedTurnOrders = new Set();
    this.startPromise = null;
  }

  async prefetchJWT() {
    if (this.getCachedJWT()) {
      return this.getCachedJWT();
    }
    if (!this.tokenFetchPromise) {
      this.tokenFetchPromise = this.fetchJWT().finally(() => {
        this.tokenFetchPromise = null;
      });
    }
    return this.tokenFetchPromise;
  }

  getCachedJWT() {
    if (
      this.tokenCache
      && !this.tokenCache.used
      && Date.now() < this.tokenCache.expiresAt - TOKEN_REFRESH_BUFFER_MS
    ) {
      return this.tokenCache.token;
    }
    return null;
  }

  async start({
    onTranscript,
    onDiarizedTranscript,
    onFinal,
    onError,
    onConnected,
    onDisconnected,
    sampleRate = 16000,
    mode = 'default',
  }) {
    // Serialize starts — AssemblyAI temp tokens are one-time use.
    if (this.startPromise) {
      try {
        await this.startPromise;
      } catch {
        // previous start failed; continue
      }
    }

    this.startPromise = this._startSession({
      onTranscript,
      onDiarizedTranscript,
      onFinal,
      onError,
      onConnected,
      onDisconnected,
      sampleRate,
      mode,
    }).finally(() => {
      this.startPromise = null;
    });

    return this.startPromise;
  }

  async _startSession({
    onTranscript,
    onDiarizedTranscript,
    onFinal,
    onError,
    onConnected,
    onDisconnected,
    sampleRate,
    mode,
  }) {
    if (this.transcriber) {
      await this._forceCloseTranscriber();
    }

    this.onTranscript = onTranscript;
    this.onDiarizedTranscript = onDiarizedTranscript;
    this.onFinal = onFinal;
    this.onError = onError;
    this.onConnected = onConnected;
    this.onDisconnected = onDisconnected;
    this.sourceSampleRate = sampleRate;
    this.sampleRate = TARGET_SAMPLE_RATE;
    this.resampler = sampleRate === TARGET_SAMPLE_RATE
      ? null
      : new LinearResampler(sampleRate, TARGET_SAMPLE_RATE);
    this.mode = mode;
    this.finalTranscript = '';
    this.interimTranscript = '';
    this.interimSpeaker = 'UU';
    this.utterances = [];
    this.intentionalClose = false;
    this.recognitionReady = false;
    this.audioQueue = [];
    this.pendingPcmChunks = [];
    this.pendingPcmSamples = 0;
    this.finalizedTurnOrders = new Set();

    try {
      // Mint a fresh one-time token for this session only.
      this.tokenCache = null;
      const token = await this.fetchJWT();
      await this.connect(token);
    } catch (err) {
      const wrapped = toError(err);
      console.error('[AssemblyAI] start failed:', wrapped.message, err);
      if (this.onError) this.onError(wrapped);
    }
  }

  async getJWT() {
    const cached = this.getCachedJWT();
    if (cached) return cached;
    return this.prefetchJWT();
  }

  async fetchJWT() {
    const data = await apiPost('/api/assemblyai/token', {});
    if (!data?.token) {
      throw new Error('AssemblyAI token response missing token');
    }
    this.tokenCache = {
      token: data.token,
      expiresAt: Date.now() + TOKEN_TTL_SECONDS * 1000,
      used: false,
    };
    return data.token;
  }

  consumeCachedToken() {
    if (this.tokenCache) {
      this.tokenCache.used = true;
    }
  }

  getTranscriberParams(token) {
    const isAdmission = this.mode === 'admission';
    return {
      token,
      sampleRate: TARGET_SAMPLE_RATE,
      speechModel: 'universal-3-5-pro',
      // Streaming API: steer EN+AR code-switching (not language_code / code_switching — those are async-only).
      languageCodes: ['ar', 'en'],
      languageDetection: true,
      prompt:
        'Jordan hospital nursing documentation. Bilingual conversation in Arabic and English with clinical terminology.',
      formatTurns: true,
      continuousPartials: true,
      includePartialTurns: true,
      mode: 'balanced',
      domain: 'medical-v1',
      connectTimeout: 15000,
      maxConnectionRetries: 2,
      connectionRetryDelay: 750,
      ...(isAdmission
        ? {
            speakerLabels: true,
            maxSpeakers: 2,
          }
        : {}),
    };
  }

  notifyError(error) {
    if (this.intentionalClose) return;
    const err = toError(error, 'AssemblyAI transcription error');
    console.error('[AssemblyAI] stream error:', err.message, error);
    if (this.onError) this.onError(err);
  }

  async connect(token) {
    this.consumeCachedToken();

    const transcriber = new StreamingTranscriber(this.getTranscriberParams(token));
    this.transcriber = transcriber;

    transcriber.on('open', () => {
      this.recognitionReady = true;
      this.flushAudioQueue();
      if (this.onConnected) this.onConnected();
    });

    transcriber.on('turn', (turn) => {
      this.handleTurn(turn);
    });

    transcriber.on('close', (code, reason) => {
      this.recognitionReady = false;
      this.transcriber = null;
      if (!this.intentionalClose && this.onDisconnected) {
        this.onDisconnected(this.finalTranscript.trim());
      } else if (!this.intentionalClose && reason) {
        console.warn('[AssemblyAI] closed:', code, reason);
      }
    });

    try {
      await transcriber.connect();
    } catch (err) {
      console.error('[AssemblyAI] connect failed:', err);
      this.recognitionReady = false;
      this.transcriber = null;
      throw toError(err);
    }

    transcriber.on('error', (error) => {
      this.notifyError(error);
    });
  }

  appendUtterance(speaker, text) {
    if (!text) return;
    const last = this.utterances[this.utterances.length - 1];
    if (last && last.speaker === speaker && last.isFinal) {
      last.text = `${last.text} ${text}`.trim();
      return;
    }
    this.utterances.push({ speaker, text, isFinal: true });
  }

  emitDiarized() {
    if (!this.onDiarizedTranscript) return;
    this.onDiarizedTranscript({
      utterances: [...this.utterances],
      interim: this.interimTranscript
        ? { speaker: this.interimSpeaker, text: this.interimTranscript }
        : null,
      flat: this.getLiveTranscript(),
    });
  }

  handleTurn(turn) {
    const text = (turn?.transcript || '').trim();
    const speaker = mapAssemblySpeaker(turn?.speaker_label);
    const turnOrder = turn?.turn_order;
    const isFinalTurn = Boolean(turn?.end_of_turn) && Boolean(turn?.turn_is_formatted);

    if (!text && !isFinalTurn) return;

    if (isFinalTurn) {
      if (turnOrder != null && this.finalizedTurnOrders.has(turnOrder)) {
        return;
      }
      if (turnOrder != null) {
        this.finalizedTurnOrders.add(turnOrder);
      }

      if (text) {
        this.finalTranscript += `${text} `;
        if (this.mode === 'admission') {
          this.appendUtterance(speaker, text);
        }
      }

      this.interimTranscript = '';
      this.interimSpeaker = 'UU';

      if (this.onTranscript) {
        this.onTranscript({
          interim: '',
          final: this.finalTranscript,
        });
      }
      this.emitDiarized();
      return;
    }

    this.interimTranscript = text;
    this.interimSpeaker = speaker || 'UU';

    if (this.onTranscript) {
      this.onTranscript({
        interim: this.interimTranscript,
        final: this.finalTranscript,
      });
    }
    this.emitDiarized();
  }

  getUtterances() {
    return [...this.utterances];
  }

  /** AssemblyAI requires each binary frame to cover 50–1000 ms of audio. */
  getMinSendSamples() {
    return Math.ceil(this.sampleRate * 0.05);
  }

  getTargetSendSamples() {
    // Prefer ~100 ms chunks for headroom above the 50 ms floor.
    return Math.ceil(this.sampleRate * 0.1);
  }

  getMaxSendSamples() {
    return Math.floor(this.sampleRate * 1.0);
  }

  appendPendingPcm(pcm16Buffer) {
    const samples = new Int16Array(pcm16Buffer);
    if (!samples.length) return;
    this.pendingPcmChunks.push(samples);
    this.pendingPcmSamples += samples.length;
  }

  takePendingPcm(sampleCount) {
    if (sampleCount <= 0 || this.pendingPcmSamples < sampleCount) return null;

    const out = new Int16Array(sampleCount);
    let offset = 0;

    while (offset < sampleCount && this.pendingPcmChunks.length) {
      const chunk = this.pendingPcmChunks[0];
      const need = sampleCount - offset;
      if (chunk.length <= need) {
        out.set(chunk, offset);
        offset += chunk.length;
        this.pendingPcmChunks.shift();
      } else {
        out.set(chunk.subarray(0, need), offset);
        this.pendingPcmChunks[0] = chunk.subarray(need);
        offset += need;
      }
    }

    this.pendingPcmSamples -= sampleCount;
    return out.buffer;
  }

  flushPendingAudio({ force = false } = {}) {
    if (!this.transcriber || !this.recognitionReady) return;

    const minSamples = this.getMinSendSamples();
    const targetSamples = this.getTargetSendSamples();
    const maxSamples = this.getMaxSendSamples();

    while (this.pendingPcmSamples >= (force ? minSamples : targetSamples)) {
      const count = Math.min(this.pendingPcmSamples, maxSamples);
      // Keep a small remainder unless forcing flush on stop.
      const sendCount = force
        ? count
        : Math.min(count, Math.max(targetSamples, minSamples));
      if (sendCount < minSamples) break;

      const frame = this.takePendingPcm(sendCount);
      if (!frame) break;

      try {
        this.transcriber.sendAudio(frame);
      } catch (err) {
        this.notifyError(err);
        break;
      }
    }

    // On force-stop, pad a leftover <50 ms frame with silence so it can be sent.
    if (force && this.pendingPcmSamples > 0 && this.pendingPcmSamples < minSamples) {
      const padded = new Int16Array(minSamples);
      const leftoverCount = this.pendingPcmSamples;
      const leftover = this.takePendingPcm(leftoverCount);
      if (leftover) {
        padded.set(new Int16Array(leftover), 0);
        try {
          this.transcriber.sendAudio(padded.buffer);
        } catch {
          // ignore flush errors on stop
        }
      }
    }
  }

  flushAudioQueue() {
    this.flushPendingAudio();
  }

  /**
   * Accepts Float32 PCM ArrayBuffers from existing capture code and converts
   * to Int16 PCM for AssemblyAI inside the STT layer only.
   * Chunks are buffered until each sent frame is at least 50 ms (AssemblyAI rule).
   */
  sendAudio(audioChunk) {
    let pcm16;
    try {
      let float32 = audioChunk instanceof Float32Array
        ? audioChunk
        : new Float32Array(audioChunk);
      if (this.resampler) {
        float32 = this.resampler.process(float32);
        if (!float32.length) return;
      }
      pcm16 = float32ToPcm16(float32);
    } catch (err) {
      this.notifyError(err);
      return;
    }

    this.appendPendingPcm(pcm16);

    if (!this.transcriber || !this.recognitionReady) {
      // Cap backlog (~3s) while waiting for the socket.
      const maxPending = this.sampleRate * 3;
      while (this.pendingPcmSamples > maxPending && this.pendingPcmChunks.length > 1) {
        const dropped = this.pendingPcmChunks.shift();
        this.pendingPcmSamples -= dropped.length;
      }
      return;
    }

    this.flushPendingAudio();
  }

  getLiveTranscript() {
    const parts = [this.finalTranscript.trim(), this.interimTranscript.trim()].filter(Boolean);
    return parts.join(' ').trim();
  }

  clearTranscript() {
    this.finalTranscript = '';
    this.interimTranscript = '';
    this.utterances = [];
  }

  async _forceCloseTranscriber() {
    const previous = this.transcriber;
    this.flushPendingAudio({ force: true });
    this.transcriber = null;
    this.recognitionReady = false;
    this.audioQueue = [];
    this.pendingPcmChunks = [];
    this.pendingPcmSamples = 0;
    if (!previous) return;
    try {
      await previous.close();
    } catch {
      // ignore
    }
  }

  stop() {
    this.intentionalClose = true;
    this.flushPendingAudio({ force: true });
    this.recognitionReady = false;
    this.audioQueue = [];
    this.pendingPcmChunks = [];
    this.pendingPcmSamples = 0;
    this.clearTranscript();
    if (this.transcriber) {
      this.transcriber.close().catch(() => {});
    }
  }

  disconnect() {
    this.intentionalClose = true;
    this.recognitionReady = false;
    this.audioQueue = [];
    this.pendingPcmChunks = [];
    this.pendingPcmSamples = 0;
    this.clearTranscript();
    if (this.transcriber) {
      this.transcriber.close().catch(() => {});
      this.transcriber = null;
    }
  }
}

const assemblyAiService = new AssemblyAiService();
export default assemblyAiService;

export function mapSpeakerLabel(speaker, speakerMap) {
  if (speaker === 'UU' || !speaker) return 'Unknown';
  return speakerMap[speaker] || speaker;
}

export const DEFAULT_SPEAKER_MAP = {
  S1: 'Nurse',
  S2: 'Patient',
};
